import React, { useEffect, useState } from 'react';

const COOKIE_NAME = 'graphite-cookie-consent';
const GA_MEASUREMENT_ID = 'G-LMGKP6N3FW';

declare global {
  interface Window {
    dataLayer: any[];
    gtag: (...args: any[]) => void;
  }
}

export const CookieConsent: React.FC = () => {
  const [showBanner, setShowBanner] = useState(false);
  const [currentConsent, setCurrentConsent] = useState<string | null>(null);

  useEffect(() => {
    // Check if user has already made a choice
    let consent: string | null = null;
    try {
      consent = localStorage.getItem(COOKIE_NAME);
      setCurrentConsent(consent);
    } catch (error) {
      // localStorage not available (Safari private mode, storage disabled, etc.)
      // Treat as no consent and show banner
      console.warn('localStorage not available, showing cookie consent banner', error);
    }

    if (consent === null) {
      // No choice made yet, show banner
      setShowBanner(true);
    } else if (consent === 'accepted') {
      // User previously accepted, load GA
      loadGoogleAnalytics();
    }
    // If consent === 'rejected', don't show banner and don't load GA
  }, []);

  const loadGoogleAnalytics = () => {
    // Only load GA in production environment
    // Prevents polluting analytics in development/test and leaking developer behavior
    if (process.env.NODE_ENV !== 'production') {
      console.log('[CookieConsent] Skipping GA load in non-production environment');
      return;
    }

    // Re-enable GA in case it was previously disabled
    (window as any)[`ga-disable-${GA_MEASUREMENT_ID}`] = false;

    // Check if GA script is already loaded (prevent duplicate scripts)
    const existingScript = document.querySelector(`script[src*="googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}"]`);
    if (!existingScript) {
      // Load gtag.js script only if not already present
      const script = document.createElement('script');
      script.async = true;
      script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
      document.head.appendChild(script);
    }

    // Initialize or reinitialize gtag
    window.dataLayer = window.dataLayer || [];
    if (!window.gtag) {
      window.gtag = function() {
        window.dataLayer.push(arguments);
      };
    }

    // Always send new pageview/config when re-enabling (but won't duplicate script)
    window.gtag('js', new Date());
    window.gtag('config', GA_MEASUREMENT_ID, {
      anonymize_ip: true, // Anonymize IPs for better privacy
    });
  };

  const disableGoogleAnalytics = () => {
    // Disable Google Analytics immediately (GDPR compliance)
    // https://developers.google.com/analytics/devguides/collection/ga4/disable-analytics
    (window as any)[`ga-disable-${GA_MEASUREMENT_ID}`] = true;

    // Delete GA cookies to stop tracking
    const cookiesToDelete = ['_ga', '_gat', '_gid', `_ga_${GA_MEASUREMENT_ID.replace('G-', '')}`];
    cookiesToDelete.forEach(cookieName => {
      document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
      document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=${window.location.hostname};`;
      document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=.${window.location.hostname};`;
    });

    // If gtag exists, send opt-out event
    if (window.gtag) {
      window.gtag('consent', 'update', {
        'analytics_storage': 'denied'
      });
    }
  };

  const handleAccept = () => {
    try {
      localStorage.setItem(COOKIE_NAME, 'accepted');
      setCurrentConsent('accepted');
    } catch (error) {
      // localStorage not available (Safari private mode, QuotaExceededError, etc.)
      // Still update state and load GA, but consent won't persist
      console.warn('Could not save consent to localStorage', error);
      setCurrentConsent('accepted');
    }
    setShowBanner(false);
    loadGoogleAnalytics();
  };

  const handleReject = () => {
    try {
      localStorage.setItem(COOKIE_NAME, 'rejected');
      setCurrentConsent('rejected');
    } catch (error) {
      // localStorage not available (Safari private mode, QuotaExceededError, etc.)
      // Still update state to respect user choice in current session
      console.warn('Could not save consent to localStorage', error);
      setCurrentConsent('rejected');
    }
    setShowBanner(false);

    // Immediately disable GA if it's already loaded (GDPR compliance)
    disableGoogleAnalytics();
  };

  const handleManageCookies = () => {
    setShowBanner(true);
  };

  // Show persistent "Cookie Settings" link when banner is hidden and user has made a choice
  // GDPR/CCPA compliance: Users must be able to withdraw consent "at any time"
  // Positioned at bottom of page content (not viewport) to avoid overlap
  if (!showBanner && currentConsent !== null) {
    return (
      <div
        style={{
          width: '100%',
          textAlign: 'center',
          padding: '10px 0',
        }}
      >
        <button
          type="button"
          onClick={handleManageCookies}
          aria-label="Cookie Settings"
          style={{
            background: 'none',
            border: 'none',
            color: '#9ca3af',
            fontSize: '12px',
            textDecoration: 'none',
            cursor: 'pointer',
            padding: 0,
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.textDecoration = 'underline';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.textDecoration = 'none';
          }}
        >
          Cookie Settings
        </button>
      </div>
    );
  }

  // Don't show anything if no consent choice has been made yet (banner will show below)
  if (!showBanner) {
    return null;
  }

  return (
    <div
      style={{
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        backgroundColor: '#2c3e50',
        color: '#ecf0f1',
        padding: '20px',
        boxShadow: '0 -2px 10px rgba(0,0,0,0.2)',
        zIndex: 10000,
        display: 'flex',
        flexDirection: 'column',
        gap: '15px',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
      }}
    >
      <div style={{ maxWidth: '1200px', margin: '0 auto', width: '100%' }}>
        <div style={{ marginBottom: '15px' }}>
          <h3 style={{ margin: '0 0 10px 0', fontSize: '18px', fontWeight: 600 }}>
            Cookie Consent
          </h3>
          {currentConsent && (
            <p style={{ margin: '0 0 10px 0', fontSize: '13px', color: '#f39c12', fontWeight: 500 }}>
              Current choice: Analytics cookies are {currentConsent === 'accepted' ? 'enabled' : 'disabled'}.
              {currentConsent === 'accepted' && ' Rejecting will stop tracking immediately.'}
            </p>
          )}
          <p style={{ margin: 0, fontSize: '14px', lineHeight: '1.5', color: '#bdc3c7' }}>
            We use cookies to improve your experience and analyze site traffic with Google Analytics.
            Your data is anonymized and helps us understand how visitors use Graphite.
            You can accept or reject analytics cookies below.
          </p>
        </div>

        <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'center' }}>
          <button
            onClick={handleAccept}
            style={{
              backgroundColor: '#27ae60',
              color: 'white',
              border: 'none',
              padding: '10px 24px',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: 600,
              transition: 'background-color 0.2s',
            }}
            onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#229954'}
            onMouseOut={(e) => e.currentTarget.style.backgroundColor = '#27ae60'}
          >
            Accept Cookies
          </button>

          <button
            onClick={handleReject}
            style={{
              backgroundColor: 'transparent',
              color: '#ecf0f1',
              border: '1px solid #7f8c8d',
              padding: '10px 24px',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: 600,
              transition: 'border-color 0.2s, background-color 0.2s',
            }}
            onMouseOver={(e) => {
              e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.1)';
              e.currentTarget.style.borderColor = '#95a5a6';
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.backgroundColor = 'transparent';
              e.currentTarget.style.borderColor = '#7f8c8d';
            }}
          >
            Reject Cookies
          </button>

          <span style={{ fontSize: '12px', color: '#95a5a6', marginLeft: 'auto' }}>
            Essential cookies are always enabled for the app to function.
          </span>
        </div>
      </div>
    </div>
  );
};
